import {
    Button,
    CustomTable,
    OptionType,
    Pagination,
    TableType,
    CodebookType,
    ConfirmationModal,
} from "modules/common";
import { useContext, useMemo, useState } from "react";
import { SortingRule } from "react-table";
import { CodebookEntry, CodebookEntryResponse } from "services/codebooks";
import { CodebookDetailsModal } from "./CodebooksModal";
import { useDeactivateCodebookEntry, useActivateCodebookEntry } from "../hooks/codebooksHooks";
import { DEFAULT_PAGE_SIZE_OPTION } from "helpers/PaginationHelper";
import { AccessibilityContext } from "views/AccessibilityContext";

export interface ICodebooksTable {
    codebooks?: CodebookEntryResponse;
    isLoading?: boolean;
    setSortBy?: (sortBy: SortingRule<object>) => void;
    page?: number;
    pageSize?: number;
    onPageSizeChange?: (value: number) => void;
    onPageChange?: (page: number) => void;
    className?: string;
    codebookType: CodebookType;
}

const defaultTableColumns = (codebookType: CodebookType) => {
    const columnArray = [];

    columnArray.push({
        Header: "NAZIV",
        accessor: "name",
        width: 3,
        Cell: ({ row }: any) => {
            const { original } = row;
            return <span className="modal-name no-left-padding">{original.name}</span>;
        },
    });

    if (codebookType === CodebookType.Intervention) {
        columnArray.push({
            Header: "KOD",
            accessor: "code",
            width: 3,
            Cell: ({ row }: any) => {
                const { original } = row;
                return <span className="modal-name">{original.code}</span>;
            },
        });
        columnArray.push({
            Header: "CILJNA GRUPA",
            accessor: "targetGroup",
            width: 3,
            Cell: ({ row }: any) => {
                const { original } = row;
                return <span className="modal-name">{original.targetGroup}</span>;
            },
        });
    }

    if (codebookType === CodebookType.ProductionType) {
        columnArray.push({
            Header: "STRUČNO PODRUČJE",
            accessor: "professionalArea",
            width: 3,
            Cell: ({ row }: any) => {
                const { original } = row;
                return <span className="modal-name">{original.professionalAreaName}</span>;
            },
        });
    }

    return columnArray;
};

export const CodebooksTable = ({
    codebooks,
    isLoading,
    setSortBy,
    page,
    pageSize,
    onPageSizeChange,
    onPageChange,
    className,
    codebookType,
}: ICodebooksTable) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState<CodebookEntry>();
    const deactivate = useDeactivateCodebookEntry();
    const activate = useActivateCodebookEntry();
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);

     const { lowVision } = useContext(AccessibilityContext);

    const tableColumns = useMemo(() => {
        let columns = defaultTableColumns(codebookType);
        if (!setSortBy) {
            columns = columns.map(column => ({ ...column, disableSortBy: true }));
        }

        const actionColumn = {
            Header: "",
            accessor: "info",
            id: "info",
            disableSortBy: true,
            width: 3,
            className: "actions-cell",
            Cell: ({ row }: any) => {
                return (
                    <>
                        <Button
                            type="button"
                            size="small"
                            variant="primary-ghost"
                            lowVision={lowVision}
                            onClick={() => {
                                setModalOpen(true);
                                setSelectedRow(row.original);
                            }}
                        >
                            <span>Uredi</span>
                        </Button>
                        <Button
                            type="button"
                            size="small"
                            variant={row.original.isActive ? "warning" : "secondary"}
                            onClick={() => {
                                setSelectedRow(row.original);
                                setShowConfirmationModal(true);
                            }}
                        >
                            <span>{row.original.isActive ? "Deaktiviraj" : "Aktiviraj"}</span>
                        </Button>
                    </>
                );
            },
        };
        columns = [...columns, actionColumn];
        return columns;
    }, [codebookType, setSortBy]);

    const activePageSize = useMemo(() => {
        return pageSize
            ? { value: pageSize.toString(), label: pageSize.toString() }
            : DEFAULT_PAGE_SIZE_OPTION;
    }, [pageSize]);

    const classNameString = useMemo(() => {
        return `codebooks-table ${className ?? ""}`;
    }, [className]);

    const handlePageSizeChange = (option: OptionType | null) => {
        onPageSizeChange &&
            onPageSizeChange(parseInt(option?.value ?? DEFAULT_PAGE_SIZE_OPTION.value));
    };

    const handlePageClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
        const target = event.currentTarget ?? event.target;
        onPageChange && onPageChange(Number(parseInt(target.id)));
    };

    const handleNexPageClick = () => {
        onPageChange && onPageChange(page ? page + 1 : 1);
    };

    const handlePrevPageClick = () => {
        onPageChange && onPageChange(page ? page - 1 : 1);
    };

    const handlePageSelect = (page: number) => {
        onPageChange && onPageChange(page);
    };

    const handleFirstPageClick = () => {
        onPageChange && onPageChange(1);
    };

    const handleLastPageClick = () => {
        if (codebooks?.items && codebooks?.items.length > 0)
            onPageChange &&
                onPageChange(Math.ceil(codebooks.total / parseInt(activePageSize.value)));
    };

    const onChangeStatus = async () => {
        const payload = {
            codebookGuid: selectedRow?.guid ?? "",
            codebookType: codebookType,
        };

        if (!selectedRow) {
            return;
        }

        if (selectedRow.isActive) {
            await deactivate.mutateAsync(payload);
            setShowConfirmationModal(false);
            return;
        }
        await activate.mutateAsync(payload);
        setShowConfirmationModal(false);
    };

    return (
        <div className={classNameString}>
            <CustomTable
                flex
                data={codebooks?.items ?? []}
                columns={tableColumns}
                loading={isLoading}
                setSortBy={setSortBy}
                tableType={TableType.AdministrationTable}
            />

            {onPageChange && codebooks?.items && codebooks.items.length > 0 && (
                <Pagination
                    className="mt-24"
                    currentPage={page ?? 1}
                    numberOfPages={Math.ceil(codebooks.total / parseInt(activePageSize.value))}
                    pageNeighbours={1}
                    pageSize={activePageSize}
                    onPageSizeChange={handlePageSizeChange}
                    handlePageClick={handlePageClick}
                    handlePageSelect={handlePageSelect}
                    next={handleNexPageClick}
                    prev={handlePrevPageClick}
                    first={handleFirstPageClick}
                    last={handleLastPageClick}
                />
            )}

            {modalOpen && (
                <CodebookDetailsModal
                    isOpen={modalOpen}
                    handleClose={() => setModalOpen(false)}
                    codebook={selectedRow}
                    codebookType={codebookType}
                />
            )}
            <ConfirmationModal
                isActive={showConfirmationModal}
                onConfirm={onChangeStatus}
                onCancel={() => setShowConfirmationModal(false)}
                content={`Želite li ${selectedRow?.isActive ? "de" : ""}aktivirati unos ${
                    selectedRow?.name
                }?`}
                confirmText={selectedRow?.isActive ? "DEAKTIVIRAJ" : "AKTIVIRAJ"}
                cancelText="ODUSTANI"
            />
        </div>
    );
};
